/**
 * Preferred Account QR path on browsers that support BarcodeDetector (Android
 * Chrome, etc.): getUserMedia + native detect loop.
 *
 * Mirrored from auth0-sso/src/utils/nativeQrScan.js — html5-qrcode/ZXing often
 * shows a preview but never decodes dense JWT Account QRs on phone cameras.
 */

type BarcodeDetectorCtor = {
  new (options?: { formats?: string[] }): {
    detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>;
  };
  getSupportedFormats: () => Promise<string[]>;
};

const getBarcodeDetector = (): BarcodeDetectorCtor | undefined =>
  (window as Window & { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;

export const supportsNativeQrScan = async (): Promise<boolean> => {
  try {
    const BarcodeDetector = getBarcodeDetector();
    if (!navigator.mediaDevices?.getUserMedia || !BarcodeDetector?.getSupportedFormats) {
      return false;
    }
    const formats = await BarcodeDetector.getSupportedFormats();
    return formats.includes('qr_code');
  } catch {
    return false;
  }
};

const scoreCameraLabel = (label = '') => {
  const l = label.toLowerCase();
  let score = 0;
  if (/back|rear|environment|facing back/.test(l)) score += 10;
  if (/front|user|facing front/.test(l)) score -= 20;
  // Ultra-wide looks fine at distance but won't focus a QR up close.
  if (/ultra|wide angle|wide-angle|\bwide\b/.test(l)) score -= 15;
  return score;
};

const pickRearDeviceId = async (): Promise<string | null> => {
  // Labels are often empty until after a permission grant.
  try {
    const warm = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    });
    warm.getTracks().forEach((t) => t.stop());
  } catch {
    // facingMode start below may still work
  }

  try {
    const videos = (await navigator.mediaDevices.enumerateDevices()).filter(
      (d) => d.kind === 'videoinput',
    );
    if (!videos.length) return null;

    const labeled = videos.filter((d) => (d.label || '').trim());
    if (labeled.length) {
      return [...labeled].sort(
        (a, b) => scoreCameraLabel(b.label) - scoreCameraLabel(a.label),
      )[0].deviceId;
    }
    // Android usually lists front first; last entry is typically main rear.
    return videos[videos.length - 1].deviceId;
  } catch {
    return null;
  }
};

type FocusCapable = MediaTrackCapabilities & {
  focusMode?: string[];
  zoom?: { min?: number; max?: number };
};

const improveTrack = async (track: MediaStreamTrack): Promise<void> => {
  try {
    const caps = (track.getCapabilities?.() || {}) as FocusCapable;
    const constraints: MediaTrackConstraints & {
      focusMode?: string;
      advanced?: Array<Record<string, unknown>>;
    } = {};
    if (caps.focusMode?.includes?.('continuous')) {
      constraints.focusMode = 'continuous';
    }
    if (caps.zoom) {
      const min = caps.zoom.min ?? 1;
      const max = caps.zoom.max ?? 1;
      if (max > min) {
        constraints.advanced = [{ zoom: Math.min(Math.max(2, min), max) }];
      }
    }
    if (Object.keys(constraints).length) {
      await track.applyConstraints(constraints);
    }
  } catch {
    // unsupported on many devices
  }
};

/** Open the rear camera into `videoEl`. Returns the MediaStream. */
export const startNativeCamera = async (
  videoEl: HTMLVideoElement,
): Promise<MediaStream> => {
  const deviceId = await pickRearDeviceId();
  const attempts: Array<boolean | MediaTrackConstraints> = [
    deviceId && {
      deviceId: { exact: deviceId },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    { facingMode: 'environment' },
    true,
  ].filter(Boolean) as Array<boolean | MediaTrackConstraints>;

  let lastErr: unknown;
  for (const video of attempts) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video,
        audio: false,
      });
      videoEl.srcObject = stream;
      videoEl.setAttribute('playsinline', 'true');
      videoEl.muted = true;
      await videoEl.play();

      const track = stream.getVideoTracks()[0];
      if (track) {
        await improveTrack(track);
      }
      return stream;
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error('getUserMedia failed');
};

export const stopNativeCamera = (stream: MediaStream | null): void => {
  try {
    stream?.getTracks?.().forEach((t) => t.stop());
  } catch {
    // ignore
  }
};

/** Poll BarcodeDetector on a <video>. Returns stop(). */
export const startNativeDetectLoop = (
  videoEl: HTMLVideoElement,
  onDecoded: (rawValue: string) => void,
): (() => void) => {
  const BarcodeDetector = getBarcodeDetector();
  if (!BarcodeDetector) {
    return () => undefined;
  }

  const detector = new BarcodeDetector({ formats: ['qr_code'] });
  let stopped = false;
  let busy = false;

  const tick = async () => {
    if (stopped || busy) return;
    if (!videoEl || videoEl.readyState < 2 || !videoEl.videoWidth) return;
    busy = true;
    try {
      const codes = await detector.detect(videoEl);
      if (!stopped && codes?.[0]?.rawValue) {
        onDecoded(codes[0].rawValue);
      }
    } catch {
      // frame temporarily unavailable
    } finally {
      busy = false;
    }
  };

  const timer = setInterval(tick, 250);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
};
